/**
 * Puzzle bank index.
 *
 * Each category lives in its own module and exports a const ReadonlyArray of
 * uppercase puzzle strings. Categories are picked from the show's standing
 * roster of "shape categories" (Phrase, Person, Place, Thing, Title, Quotation,
 * Event, Same Letter).
 *
 * Sourcing principles (per the dispatch brief):
 *   - Public-domain idioms / proverbs (Bartlett's pre-1928, classical sayings
 *     in wide common use long before 1928, generic phrasebook material).
 *   - Wikidata + Wikipedia commons for Place / Person / Thing (subjects only,
 *     no copied prose — we use the bare proper noun or title).
 *   - LLM-generated originals to round out gaps, audited for era-fit.
 *   - DO NOT scrape show puzzle databases. The mechanic is public domain;
 *     specific show puzzles are creative expression.
 *
 * Era-fit: no post-2000 references, no internet memes, no tech jargon. The
 * audience is 70-95+; the puzzle bank should feel like the show without
 * being the show.
 */
import type { Puzzle, PuzzleCategoryId, PuzzleCategoryMeta } from '../types';

import { PHRASES } from './phrases';
import { PEOPLE } from './people';
import { PLACES } from './places';
import { THINGS } from './things';
import { TITLES } from './titles';
import { QUOTATIONS } from './quotations';
import { EVENTS } from './events';
import { SAME_LETTER } from './same_letter';

export const CATEGORIES: ReadonlyArray<PuzzleCategoryMeta> = [
  { id: 'phrase', label: 'Phrase', hint: 'a familiar saying' },
  { id: 'person', label: 'Person', hint: 'someone you may know' },
  { id: 'place', label: 'Place', hint: 'somewhere on the map' },
  { id: 'thing', label: 'Thing', hint: 'an everyday object or idea' },
  { id: 'title', label: 'Title', hint: 'the name of a song, book, or film' },
  { id: 'quotation', label: 'Quotation', hint: 'something someone said' },
  { id: 'event', label: 'Event', hint: 'a moment in time' },
  { id: 'same_letter', label: 'Same Letter', hint: 'every word starts the same' },
];

const BANK: Record<PuzzleCategoryId, ReadonlyArray<string>> = {
  phrase: PHRASES,
  person: PEOPLE,
  place: PLACES,
  thing: THINGS,
  title: TITLES,
  quotation: QUOTATIONS,
  event: EVENTS,
  same_letter: SAME_LETTER,
};

export function puzzlesFor(category: PuzzleCategoryId): ReadonlyArray<Puzzle> {
  return BANK[category].map((text) => ({ category, text: text.toUpperCase() }));
}

export function allPuzzles(): ReadonlyArray<Puzzle> {
  const all: Puzzle[] = [];
  for (const meta of CATEGORIES) {
    all.push(...puzzlesFor(meta.id));
  }
  return all;
}

/** Deterministic 32-bit FNV-1a — same seed → same puzzle pick everywhere. */
export function fnv1a(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Today's date as the seed string `YYYYMMDD` in local time. */
export function todaySeed(now: Date = new Date()): string {
  const yyyy = now.getFullYear().toString().padStart(4, '0');
  const mm = (now.getMonth() + 1).toString().padStart(2, '0');
  const dd = now.getDate().toString().padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

/**
 * Pick today's puzzle. The category rotates by day-of-year (so we don't see
 * the same category every Monday), and the puzzle index within the category
 * is determined by the date hash.
 */
export function todayPuzzle(now: Date = new Date()): Puzzle {
  const seed = todaySeed(now);
  // Day-of-year: ~0..366 — rotate categories so each day picks a different one.
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - start.getTime();
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  const category = CATEGORIES[dayOfYear % CATEGORIES.length]!;
  const list = puzzlesFor(category.id);
  const idx = fnv1a(seed + ':' + category.id) % list.length;
  return list[idx]!;
}

/** Pick a practice puzzle from a fresh random across all categories. */
export function practicePuzzle(rand: () => number = Math.random): Puzzle {
  const meta = CATEGORIES[Math.floor(rand() * CATEGORIES.length)]!;
  const list = puzzlesFor(meta.id);
  return list[Math.floor(rand() * list.length)]!;
}
